import { useEffect, useState } from "react";
import { getTrades, type Trade } from "../../api/client";
import { format } from "date-fns";

interface Props {
  portfolioId: number | null;
}

const fmt = (v?: number | null, decimals = 2) =>
  v == null ? "—" : new Intl.NumberFormat("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(v);

const fmtUSD = (v?: number | null) =>
  v == null
    ? "—"
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v);

export default function TradesTable({ portfolioId }: Props) {
  const [trades, setTrades] = useState<Trade[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [symbol, setSymbol] = useState("");
  const [action, setAction] = useState("");
  const limit = 50;

  useEffect(() => {
    if (!portfolioId) return;
    setLoading(true);
    setError(null);
    getTrades({
      portfolio_id: portfolioId,
      symbol: symbol || undefined,
      action: action || undefined,
      skip: page * limit,
      limit,
    })
      .then((res) => setTrades(res.data))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [portfolioId, page, symbol, action]);

  if (!portfolioId) return <p style={{ color: "#64748b" }}>Select a portfolio.</p>;

  return (
    <div>
      <h2 style={{ margin: "0 0 16px", color: "#1e293b" }}>Trades</h2>

      {/* Filters */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <input
          placeholder="Filter symbol…"
          value={symbol}
          onChange={(e) => { setSymbol(e.target.value.toUpperCase()); setPage(0); }}
          style={{ padding: "6px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 14 }}
        />
        <select
          value={action}
          onChange={(e) => { setAction(e.target.value); setPage(0); }}
          style={{ padding: "6px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 14 }}
        >
          <option value="">All actions</option>
          <option value="BUY">BUY</option>
          <option value="SELL">SELL</option>
        </select>
      </div>

      {loading && <p>Loading...</p>}
      {error && <p style={{ color: "red" }}>Error: {error}</p>}

      {!loading && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ background: "#f1f5f9" }}>
                {["Date", "Symbol", "Description", "Action", "Qty", "Price", "Proceeds", "Commission", "Realized P&L", "Exchange"].map((h) => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", whiteSpace: "nowrap", color: "#475569", fontWeight: 600 }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {trades.length === 0 && (
                <tr>
                  <td colSpan={10} style={{ padding: 24, textAlign: "center", color: "#94a3b8" }}>
                    No trades found.
                  </td>
                </tr>
              )}
              {trades.map((t) => (
                <tr
                  key={t.id}
                  style={{ borderBottom: "1px solid #f1f5f9" }}
                >
                  <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>{format(new Date(t.trade_date), "MMM d, yyyy")}</td>
                  <td style={{ padding: "8px 12px", fontWeight: 600 }}>{t.symbol}</td>
                  <td style={{ padding: "8px 12px", color: "#64748b", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{t.description}</td>
                  <td style={{ padding: "8px 12px" }}>
                    <span style={{ color: t.action === "BUY" ? "#16a34a" : "#dc2626", fontWeight: 600 }}>{t.action}</span>
                  </td>
                  <td style={{ padding: "8px 12px", textAlign: "right" }}>{fmt(t.quantity, 4)}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right" }}>{fmtUSD(t.price)}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right" }}>{fmtUSD(t.proceeds)}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right", color: "#dc2626" }}>{fmtUSD(t.commission)}</td>
                  <td style={{ padding: "8px 12px", textAlign: "right", color: (t.realized_pnl ?? 0) >= 0 ? "#16a34a" : "#dc2626" }}>
                    {fmtUSD(t.realized_pnl)}
                  </td>
                  <td style={{ padding: "8px 12px" }}>{t.exchange}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
        <button
          disabled={page === 0}
          onClick={() => setPage((p) => p - 1)}
          style={{ padding: "4px 12px", cursor: page === 0 ? "not-allowed" : "pointer" }}
        >
          ← Prev
        </button>
        <span style={{ fontSize: 13, color: "#64748b" }}>Page {page + 1}</span>
        <button
          disabled={trades.length < limit}
          onClick={() => setPage((p) => p + 1)}
          style={{ padding: "4px 12px", cursor: trades.length < limit ? "not-allowed" : "pointer" }}
        >
          Next →
        </button>
      </div>
    </div>
  );
}
